<template>
  <b-toast
    v-model="visible"
    :toaster="toaster"
    data-testid="system-task-execution-progress"
    no-auto-hide
    no-close-on-hover-pause
    solid
  >
    <template #toast-title>
      {{ toastTitle }}
    </template>

    <div
      v-if="progress"
      class="system-task-execution-progress"
    >
      <p class="mb-2">
        {{ statusMessage }}
      </p>

      <b-progress
        v-if="0 < progress.progress.total"
        :max="progress.progress.total"
        data-testid="system-task-execution-progress-bar"
      >
        <b-progress-bar
          :value="progress.progress.complete"
          variant="success"
        />
        <b-progress-bar
          :value="progress.progress.running"
          variant="primary"
          :animated="!complete"
        />
        <b-progress-bar
          :value="progress.progress.pending"
          variant="secondary"
        />
        <b-progress-bar
          :value="progress.progress.expired"
          variant="warning"
        />
        <b-progress-bar
          :value="progress.progress.error"
          variant="danger"
        />
      </b-progress>

      <div class="text-right mt-2">
        <b-btn
          size="sm"
          variant="primary"
          data-testid="system-task-execution-progress-view-details"
          @click="viewDetails"
        >
          View details
        </b-btn>
      </div>
    </div>
  </b-toast>
</template>

<script>
export default {
  name: 'SystemTaskExecutionProgress',
  props: {
    autoHideDelay: {
      type: Number,
      default: 15000,
    },
    pollInterval: {
      type: Number,
      default: 2000,
    },
    slowPollInterval: {
      type: Number,
      default: 10000,
    },
    slowPollThreshold: {
      type: Number,
      default: 30000,
    },
    systemTaskExecutionId: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    toaster: {
      type: String,
      default: 'b-toaster-bottom-right',
    },
  },
  data() {
    return {
      hideTimer: null,
      lastSystemTaskExecutionId: null,
      pollStartedAt: null,
      pollTimer: null,
      progress: null,
      visible: false,
    };
  },
  computed: {
    complete() {
      return this.progress?.complete ?? false;
    },
    statusMessage() {
      if (this.complete) {
        return 'Task complete';
      }

      const total = this.progress?.progress?.total ?? 0;
      const finished = (this.progress?.progress?.complete ?? 0) +
        (this.progress?.progress?.error ?? 0) +
        (this.progress?.progress?.expired ?? 0);

      return 0 < total ? `Processed ${finished} of ${total}` : 'Task in progress…';
    },
    toastTitle() {
      return this.title ?? this.progress?.systemTask ?? 'Task progress';
    },
  },
  watch: {
    systemTaskExecutionId(newId) {
      this.stop();

      if (newId) {
        this.start();
      }
    },
  },
  mounted() {
    if (this.systemTaskExecutionId) {
      this.start();
    }
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    fetchProgress() {
      const id = this.iriToUuid(this.lastSystemTaskExecutionId);

      this.axios.get(`/system-task-executions/${id}/progress`).then((response) => {
        this.progress = response.data;

        if (this.progress.complete) {
          this.$emit('complete', this.progress);
          this.scheduleHide();
          return;
        }

        this.schedulePoll();
      }).catch((error) => {
        this.restError(error, 'Unable to retrieve task progress');
        this.$emit('error', error);
        this.stop();
      });
    },
    schedulePoll() {
      clearTimeout(this.pollTimer);

      const elapsed = Date.now() - this.pollStartedAt;
      const interval = elapsed >= this.slowPollThreshold ? this.slowPollInterval : this.pollInterval;

      this.pollTimer = setTimeout(this.fetchProgress, interval);
    },
    scheduleHide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.visible = false;
      }, this.autoHideDelay);
    },
    start() {
      clearTimeout(this.pollTimer);
      clearTimeout(this.hideTimer);

      this.lastSystemTaskExecutionId = this.systemTaskExecutionId;
      this.progress = null;
      this.visible = true;
      this.pollStartedAt = Date.now();

      this.fetchProgress();
    },
    stop() {
      clearTimeout(this.pollTimer);
      clearTimeout(this.hideTimer);
      this.pollTimer = null;
      this.hideTimer = null;
    },
    viewDetails() {
      const route = this.$router.resolve({
        name: 'readSystemTaskExecution',
        params: {id: this.iriToUuid(this.lastSystemTaskExecutionId)},
      });

      window.open(route.href, '_blank');
    },
  },
};
</script>

<style scoped>

</style>
